import * as React from 'react';
import { observer } from 'mobx-react-lite';
import { Form, Row, Col } from 'antd';
import { SectionsComparisonNodeModel } from '../../models';
import { useNodeComparisonForm } from '../../hooks';
import { getVisibleComparisonGroups, StyleUtils } from '../../misc';
import { useAppStore } from '../../stores';
import { ContractComparisonChangeLabel, ContractComparisonPanelRenderer } from '..';
import type { ComparisonInputGroup } from '../../models/contract_comparison/ComparisonNodeBaseModel';

interface Props {
    comparisonNode: SectionsComparisonNodeModel;
}

const SectionsComparisonRenderer: React.FC<Props> = ({ comparisonNode }) => {
    const { contractComparisonStore } = useAppStore();

    const { form, formKey, onValuesChange } = useNodeComparisonForm(
        comparisonNode.newNode,
        comparisonNode.previousNode
    );

    const groups = getVisibleComparisonGroups(comparisonNode.groups, contractComparisonStore.selectedFilter);

    const renderSectionHeading = (group: ComparisonInputGroup) => {
        const pair = group.pair;

        if (!pair) {
            return null;
        }

        return (
            <Row className="contract-comparison-section-heading" gutter={10}>
                <Col style={StyleUtils.getNodeColumnStyleByPercentage(40)}>
                    {pair.kind === 'newOnly' ? 'No matching previous section' : pair.previousSection.name}
                </Col>

                <Col style={StyleUtils.getNodeColumnStyleByPercentage(40)}>
                    {pair.kind === 'previousOnly' ? 'No matching new section' : pair.newSection.name}
                </Col>

                <Col style={StyleUtils.getNodeColumnStyleByPercentage(20)}>
                    {pair.kind !== 'matched' && (
                        <ContractComparisonChangeLabel changeType={pair.kind === 'newOnly' ? 'added' : 'removed'} />
                    )}
                </Col>
            </Row>
        );
    };

    return (
        <Form id={comparisonNode.nodeId} key={formKey} form={form} onValuesChange={onValuesChange}>
            {comparisonNode.panels.map(panel => (
                <ContractComparisonPanelRenderer
                    key={panel.id}
                    panelId={panel.id}
                    panelName={panel.name}
                    nodeId={comparisonNode.nodeId}
                    groups={groups.filter(group => group.panelId === panel.id)}
                    renderGroupHeading={renderSectionHeading}
                />
            ))}
        </Form>
    );
};

export default observer(SectionsComparisonRenderer);
